import { PropsWithChildren } from 'react';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import MeetingsScheduleProvider from 'contexts/MeetingsScheduleProvider/MeetingsScheduleProvider';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import useDefaultLandingPage from 'modules/Authentication/hooks/useDefaultLandingPage';
import LoginSecretStorageUnlockGate from 'modules/EndToEndEncryption/LoginSecretStorageUnlockGate';
import NotificationOnboardingCard from 'modules/Notifications/components/NotificationOnboardingCard/NotificationOnboardingCard';
import ScheduledMeetingsWatcher from 'modules/ScheduledMeetingsWatcher/ScheduledMeetingsWatcher';
import { FeatureFlag } from 'utils/Constants';

import { useLoginSecretStorageUnlock } from './loginSecretStorageUnlock';
import useInitializeMatrix from './useInitializeMatrix';
import useMatrixContext from './useMatrixContext';
import { MatrixLoggingInStatusEnum } from './utils';

const DefaultLandingPageRedirect = () => {
  useDefaultLandingPage();
  return null;
};

const InitializeMatrix = ({ children }: PropsWithChildren) => {
  const matrixContext = useMatrixContext({ requireDefinedMatrixClient: false });
  useInitializeMatrix(matrixContext);

  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const { isRequired: isLoginUnlockRequired } = useLoginSecretStorageUnlock();

  const isLoading =
    [MatrixLoggingInStatusEnum.INITIAL, MatrixLoggingInStatusEnum.RUNNING].includes(
      matrixContext.matrixLoggingInStatus
    ) ||
    (matrixContext.matrixLoggingInStatus === MatrixLoggingInStatusEnum.FINISHED && !matrixContext.matrixClient);

  if (isLoading) return isLoginUnlockRequired ? <LoginSecretStorageUnlockGate /> : <LoadingScreen />;

  return (
    <>
      {matrixContext.matrixClient && isMessengerEnabled ? <DefaultLandingPageRedirect /> : null}
      {matrixContext.matrixClient ? (
        <MeetingsScheduleProvider>
          <ScheduledMeetingsWatcher />
          {children}
          <NotificationOnboardingCard />
        </MeetingsScheduleProvider>
      ) : (
        children
      )}
    </>
  );
};

export default InitializeMatrix;
